import { makeAutoObservable } from 'mobx'
import variables from 'variables/variables'
import { apiClient } from 'api/trpc/trpc-client'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'
import { AuthTokens } from '@gap/backend/infra-services/cognito/CognitoService'
import { isForceLogoutInProgress } from 'auth/logout-reason'

export type AuthCustomTokenSet = {
   idToken: string
   accessToken: string
   refreshToken: string
}

export default class AuthCustomTokenStore {
   refreshInterval: NodeJS.Timeout

   constructor() {
      makeAutoObservable(this)
   }

   /**
    * Saves the token set to local storage
    */
   saveTokenSet = (params: Required<AuthTokens>) => {
      const { idToken, accessToken, refreshToken } = params
      localStorage.setItem(variables.gapIdTokenLocalStorageKey, idToken)
      localStorage.setItem(variables.gapAccessTokenLocalStorageKey, accessToken)
      localStorage.setItem(variables.gapRefreshTokenLocalStorageKey, refreshToken)
   }

   /**
    * Attempts to refresh the token set. If successful, the new token set is saved to local storage
    */
   async refreshTokenSet(refreshToken: string, userId: string) {
      try {
         const result = await apiClient.user.refreshToken.mutate({ refreshToken, userId })

         if (result && result.idToken && result.accessToken && result.refreshToken) {
            this.saveTokenSet({ idToken: result.idToken, accessToken: result.accessToken, refreshToken: result.refreshToken })
         } else {
            snackbarError(result?.message || "Failed to refresh token set")
         }
      } catch {
         // A 401 here means the session is dead and trpcFetcher already started the logout
         // redirect. Anything else (a network blip) is retried on the next interval tick.
         if (!isForceLogoutInProgress()) {
            snackbarError("Failed to refresh token set")
         }
      }
   }

   /**
    * Starts a token refresh interval - attempts to refresh the token set every x minutes
    */
   startTokenRefresh(refreshToken: string, userId: string) {
      // Clear existing interval if any
      this.stopTokenRefresh();

      // Set interval to refresh token every x minutes
      this.refreshInterval = setInterval(() => {
         this.refreshTokenSet(refreshToken, userId)
      }, variables.tokenRefreshInterval)
   }

   /**
    * Stops the token refresh interval
    */
   stopTokenRefresh() {
      if (this.refreshInterval) {
         clearInterval(this.refreshInterval)
      }
   }

   removeTokens = async () => {
      localStorage.removeItem(variables.gapIdTokenLocalStorageKey)
      localStorage.removeItem(variables.gapAccessTokenLocalStorageKey)
      localStorage.removeItem(variables.gapRefreshTokenLocalStorageKey)
      this.stopTokenRefresh()
   }
}
